import React, { useCallback } from 'react';
import useArticleStore from './stores/useArticleStore';
import { searchDoctors } from './api';
import HospitalSelector from '../shared/HospitalSelector';
import DoctorSelector from '../shared/DoctorSelector';
import TagsInput from './TagsInput';
import CategoriesSelect from './CategoriesSelect';
import SEOPanel from './SEOPanel';
import ChangeHistory from './ChangeHistory';
import { useTranslations } from './contexts/TranslationContext';

export default function FormSidebar({ hospitals, scheduleAutosave, auditEntries }) {
  var t = useTranslations();
  var ownerableId = useArticleStore(function(s) { return s.ownerable_id; });
  var doctorId = useArticleStore(function(s) { return s.doctor_id; });
  var doctorName = useArticleStore(function(s) { return s.doctor_name; });
  var doctorAvatar = useArticleStore(function(s) { return s.doctor_avatar; });
  var updateField = useArticleStore(function(s) { return s.updateField; });
  var publishErrors = useArticleStore(function(s) { return s.publishErrors; });

  var handleHospitalSelect = useCallback(function(hospitalId) {
    updateField('ownerable_id', hospitalId);
    scheduleAutosave();
  }, [updateField, scheduleAutosave]);

  var handleHospitalClear = useCallback(function() {
    updateField('ownerable_id', '');
    scheduleAutosave();
  }, [updateField, scheduleAutosave]);

  var selectedDoctors = doctorId && doctorName
    ? [{ id: doctorId, name: doctorName, avatar: doctorAvatar }]
    : [];

  var handleDoctorSelect = useCallback(function(doctor) {
    updateField('doctor_id', doctor.id);
    updateField('doctor_name', doctor.full_name || doctor.name);
    updateField('doctor_avatar', doctor.avatar ? doctor.avatar.url || doctor.avatar : null);
    scheduleAutosave();
  }, [updateField, scheduleAutosave]);

  var handleDoctorRemove = useCallback(function() {
    updateField('doctor_id', '');
    updateField('doctor_name', '');
    updateField('doctor_avatar', null);
    scheduleAutosave();
  }, [updateField, scheduleAutosave]);

  return (
    <div className="form-sidebar__inner">
      <div className="form-sidebar__section" data-tour="hospital_selector">
        <h6 className="form-sidebar__heading">
          <i className="zmdi zmdi-assignment zmdi-hc-fw" /> {t.publishing_details}
        </h6>
        <div className="mb-3">
          <label className="form-label small text-muted mb-1">{t.hospital}</label>
          <HospitalSelector
            hospitals={hospitals || []}
            selectedId={ownerableId}
            onSelect={handleHospitalSelect}
            onClear={handleHospitalClear}
            errors={publishErrors && publishErrors.ownerable_id ? publishErrors.ownerable_id : null}
            translations={t}
          />
        </div>
        <div>
          <label className="form-label small text-muted mb-1">{t.doctor_label}</label>
          <DoctorSelector
            multiple={false}
            selectedDoctors={selectedDoctors}
            onSelect={handleDoctorSelect}
            onRemove={handleDoctorRemove}
            searchDoctors={searchDoctors}
            errors={publishErrors && publishErrors.doctor_id ? publishErrors.doctor_id : null}
            translations={t}
          />
        </div>
      </div>

      <div className="form-sidebar__section" data-tour="tags_panel">
        <h6 className="form-sidebar__heading">
          <i className="zmdi zmdi-labels zmdi-hc-fw" /> {t.tags}
        </h6>
        <TagsInput scheduleAutosave={scheduleAutosave} />
      </div>

      <div className="form-sidebar__section" data-tour="categories_panel">
        <h6 className="form-sidebar__heading">
          <i className="zmdi zmdi-folder zmdi-hc-fw" /> {t.categories}
        </h6>
        <CategoriesSelect scheduleAutosave={scheduleAutosave} />
      </div>

      <div className="form-sidebar__section" data-tour="seo_panel">
        <h6 className="form-sidebar__heading">
          <i className="zmdi zmdi-search zmdi-hc-fw" /> {t.seo}
        </h6>
        <SEOPanel scheduleAutosave={scheduleAutosave} />
      </div>

      {auditEntries && <ChangeHistory entries={auditEntries} />}
    </div>
  );
}
